import { Component, Input, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Logger } from '../../../core/logger.service';
import { IndeterminateProgressDialogComponent } from '../../../shared/progress/indeterminate-progress-dialog/indeterminate-progress-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SnackBarService } from '../../../core/snack-bar.service';
import { WidgetService } from '../../../shared/services/widget.service';
import { CampaignPreviewData } from './campaign-preview-models';
import { OnsiteCampaignType, TargetDevice } from '../../campaign.model';
import { CampaignService } from 'app/shared/services/campaign.service';
import { Widget } from '../../widgets.model';
import WidgetComponentHelpers from '../../widget-component-helpers';

@Component({
  selector: 'app-campaign-preview',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './campaign-preview.component.html',
  styleUrls: ['./campaign-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CampaignPreviewComponent {
  @Input() accountHasVisits: boolean;
  @Input() classComponent: string;
  private readonly logger: Logger;
  private progressDialogRef: MatDialogRef<IndeterminateProgressDialogComponent>;
  public CampaignTypes = OnsiteCampaignType;

  private _data: CampaignPreviewData;
  @Input() set data(data: CampaignPreviewData) {
    this._data = data;
  }
  get data(): CampaignPreviewData {
    return this._data;
  }

  @Input() translationKey: string = undefined;
  @Input() fillBackground = false;

  constructor(
    private readonly widgetService: WidgetService,
    private dialog: MatDialog,
    private readonly snackbarService: SnackBarService,
    private readonly campaignService: CampaignService,
  ) {}

  private showCampaignProgressDialog() {
    this.progressDialogRef = this.dialog.open(IndeterminateProgressDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
  }

  private closeCampaignProgressDialog() {
    if (this.progressDialogRef) {
      this.progressDialogRef.close();
    }
  }

  // Native-app campaigns have no website to preview on — show the label as "Quick preview" and open the
  // in-app quick preview instead of the on-site campaign preview.
  public get isNativeApp(): boolean {
    return this._data?.campaign?.targetDevice === TargetDevice.NativeApp;
  }

  public get previewLabel(): string {
    return this.isNativeApp ? 'campaign.templates.quick-preview' : this.translationKey || 'campaign.widget.btn-preview';
  }

  public previewWidget(widget: Widget): void {
    if (!WidgetComponentHelpers.hasValidStep(widget)) {
      return;
    }
    this.showCampaignProgressDialog();
    this.widgetService.prepareWidgetPreview(widget.id).subscribe(
      () => {
        this.closeCampaignProgressDialog();
        if (this.isNativeApp) {
          this.widgetService.previewWidget(
            undefined,
            widget.id,
            undefined,
            undefined,
            { campaignId: this._data.campaign?.id },
            undefined,
            this.accountHasVisits,
          );
        } else {
          this.campaignService.openCampaignPreview(
            this._data.campaign,
            this.data.accountDomainName,
            this.data.overridePreviewUrl,
            widget.id,
            this.data.urlSettings,
            this.accountHasVisits,
          );
        }
        this.snackbarService.closeSnackBar();
      },
      (error) => {
        this.closeCampaignProgressDialog();
        this.snackbarService.error('campaign.preview-error');
        this.logger.error('Widget could not be previewed: ' + JSON.stringify(error));
      },
    );
  }

  public hasValidStep(widget: Widget): boolean {
    return WidgetComponentHelpers.hasValidStep(widget);
  }
}
